import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { financingTypes } from '../data/mock';
import { Home, CreditCard, RefreshCw, Wallet, Briefcase, Banknote, ArrowRight, ArrowLeft, CheckCircle2, Shield, Loader2 } from 'lucide-react';
import ChatWidget from '../components/common/ChatWidget';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Home,
  CreditCard,
  RefreshCw,
  Wallet,
  Briefcase,
  Banknote
};

const InquiryPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Financing Type
    financingType: '',
    amount: '',

    // Step 2: Financial Data
    employmentType: '',
    netIncome: '',
    eigenkapital: '',
    desiredMonthlyRate: '',
    permanentContract: '',

    // Step 3: Personal Data
    salutation: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    email: '',
    phone: '',
    postalCode: '',

    // Step 4: Contact
    preferredContact: '',
    callbackTime: '',
    comments: '',
    privacyAccepted: false,
    newsletterAccepted: false
  });

  const totalSteps = 4;

  // Validation constants and helper functions
  const MAX_NET_INCOME = 250000;

  const sanitizeName = (value) =>
    String(value).replace(/[^A-Za-zÀ-ÖØ-öø-ÿÄÖÜäöüß '\-]/g, '');

  const sanitizePhone = (value) =>
    String(value)
      .replace(/[^\d+ ]/g, '')
      .replace(/(?!^)\+/g, '');

  const clampDigitsToMax = (value, max) => {
    const digits = String(value).replace(/[^\d]/g, '');
    if (!digits) return '';
    const n = parseInt(digits, 10);
    if (Number.isNaN(n)) return '';
    return String(Math.min(n, max));
  };

  const updateFormData = (field, value) => {
    let nextValue = value;

    if (field === 'firstName' || field === 'lastName') {
      nextValue = sanitizeName(value);
    }

    if (field === 'phone') {
      nextValue = sanitizePhone(value);
    }

    if (field === 'netIncome') {
      nextValue = clampDigitsToMax(value, MAX_NET_INCOME);
    }

    setFormData(prev => ({ ...prev, [field]: nextValue }));
  };

  // Step 1: Financing Type
  const validateStep1 = () => {
    if (!formData.financingType) {
      toast.error('Bitte wählen Sie eine Finanzierungsart aus.');
      return false;
    }
    if (!formData.amount) {
      toast.error('Bitte geben Sie den gewünschten Betrag ein.');
      return false;
    }
    return true;
  };

  // Step 2: Financial Data
  const validateStep2 = () => {
    if (!formData.employmentType) {
      toast.error('Bitte wählen Sie Ihr Beschäftigungsverhältnis aus.');
      return false;
    }
    if (!formData.netIncome) {
      toast.error('Bitte geben Sie Ihr monatliches Nettoeinkommen ein.');
      return false;
    }
    if (!formData.eigenkapital) {
      toast.error('Bitte geben Sie Ihr Eigenkapital ein.');
      return false;
    }
    if (!formData.desiredMonthlyRate) {
      toast.error('Bitte geben Sie Ihre monatliche Wunschrate ein.');
      return false;
    }
    if (!formData.permanentContract) {
      toast.error('Bitte wählen Sie aus, ob Sie ein unbefristetes Arbeitsverhältnis haben.');
      return false;
    }
    return true;
  };

  // Step 3: Personal Data
  const validateStep3 = () => {
    if (!formData.salutation) {
      toast.error('Bitte wählen Sie eine Anrede aus.');
      return false;
    }
    if (!formData.firstName) {
      toast.error('Bitte geben Sie Ihren Vornamen ein.');
      return false;
    }
    if (!formData.lastName) {
      toast.error('Bitte geben Sie Ihren Nachnamen ein.');
      return false;
    }
    if (!formData.birthDate) {
      toast.error('Bitte geben Sie Ihr Geburtsdatum ein.');
      return false;
    }
    if (!formData.phone) {
      toast.error('Bitte geben Sie Ihre Telefonnummer ein.');
      return false;
    }
    if (!formData.postalCode) {
      toast.error('Bitte geben Sie Ihre PLZ ein.');
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    if (!formData.preferredContact) {
      toast.error('Bitte wählen Sie Ihre bevorzugte Kontaktart aus.');
      return false;
    }
    if ((formData.preferredContact === 'Telefon' || formData.preferredContact === 'Beides') && !formData.callbackTime) {
      toast.error('Bitte wählen Sie Ihre bevorzugte Rückrufzeit aus.');
      return false;
    }
    if (!formData.privacyAccepted) {
      toast.error('Bitte akzeptieren Sie die Datenschutzerklärung.');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    let isValid = false;

    if (currentStep === 1) {
      isValid = validateStep1();
    } else if (currentStep === 2) {
      isValid = validateStep2();
    } else if (currentStep === 3) {
      isValid = validateStep3();
    }

    if (isValid && currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep4()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        financing_type: formData.financingType,
        amount: formData.amount,
        salutation: formData.salutation,
        first_name: formData.firstName,
        last_name: formData.lastName,
        birth_date: formData.birthDate,
        email: formData.email,
        phone: formData.phone,
        postal_code: formData.postalCode,
        employment_type: formData.employmentType,
        employer_name: "",
        employed_since: "",
        net_income: formData.netIncome,
        eigenkapital: formData.eigenkapital,
        desired_monthly_rate: formData.desiredMonthlyRate,
        permanent_contract: formData.permanentContract,
        preferred_contact: formData.preferredContact,
        callback_time: formData.callbackTime,
        comments: formData.comments,
        privacy_accepted: formData.privacyAccepted,
        newsletter_accepted: formData.newsletterAccepted
      };

      await axios.post(`${API}/inquiries/financing`, payload);

      setIsSubmitted(true);
      toast.success('Ihre Anfrage wurde erfolgreich gesendet! Wir melden uns innerhalb von 24 Stunden bei Ihnen.');
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepTitles = [
    'Finanzierungsart',
    'Finanzdaten',
    'Persönliche Daten',
    'Kontaktaufnahme'
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <main className="py-12">
        <div className="container mx-auto px-4">
          {/* Success State */}
          {isSubmitted ? (
            <div className="max-w-2xl mx-auto text-center py-16">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Vielen Dank für Ihre Anfrage!</h2>
              <p className="text-slate-600 mb-8">
                Wir haben Ihre Finanzierungsanfrage erhalten und werden uns innerhalb von 24 Stunden bei Ihnen melden.
                Eine Bestätigung wurde an Ihre E-Mail-Adresse gesendet.
              </p>
              <Button
                onClick={() => {
                  setIsSubmitted(false);
                  setCurrentStep(1);
                  setFormData({
                    financingType: '', amount: '',
                    employmentType: '', netIncome: '',
                    eigenkapital: '', desiredMonthlyRate: '', permanentContract: '',
                    salutation: '', firstName: '', lastName: '', birthDate: '',
                    email: '', phone: '', postalCode: '',
                    preferredContact: '', callbackTime: '', comments: '',
                    privacyAccepted: false, newsletterAccepted: false
                  });
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="new-inquiry-btn"
              >
                Neue Anfrage stellen
              </Button>
            </div>
          ) : (
            <>
              {/* Page Header */}
              <div className="text-center max-w-2xl mx-auto mb-12">
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Finanzierungsanfrage stellen
                </h1>
                <p className="text-slate-600">
                  Füllen Sie das Formular aus und erhalten Sie innerhalb von 24 Stunden passende Angebote.
                </p>
              </div>

              {/* Progress Steps */}
              <div className="max-w-3xl mx-auto mb-10">
                <div className="flex items-center justify-between">
                  {stepTitles.map((title, index) => (
                    <React.Fragment key={index}>
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                          currentStep > index + 1
                            ? 'bg-green-500 text-white'
                            : currentStep === index + 1
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                            : 'bg-slate-200 text-slate-500'
                        }`}>
                          {currentStep > index + 1 ? (
                            <CheckCircle2 className="w-5 h-5" />
                          ) : (
                            index + 1
                          )}
                        </div>
                        <span className={`text-xs mt-2 font-medium hidden sm:block ${
                          currentStep === index + 1 ? 'text-blue-600' : 'text-slate-500'
                        }`}>
                          {title}
                        </span>
                      </div>
                      {index < totalSteps - 1 && (
                        <div className={`flex-1 h-1 mx-2 rounded ${
                          currentStep > index + 1 ? 'bg-green-500' : 'bg-slate-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Form Container */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
              <form onSubmit={handleSubmit}>
                {/* Step 1: Financing Type */}
                {currentStep === 1 && (
                  <div className="space-y-8">
                    <div>
                      <h2 className="text-xl font-bold text-slate-900 mb-6">Wählen Sie Ihre Finanzierungsart</h2>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                        {financingTypes.map((type) => {
                          const IconComponent = iconMap[type.icon];
                          return (
                            <button
                              key={type.id}
                              type="button"
                              onClick={() => updateFormData('financingType', type.id)}
                              data-testid={`financing-type-${type.id}`}
                              className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${
                                formData.financingType === type.id
                                  ? 'border-blue-600 bg-blue-50'
                                  : 'border-slate-200 hover:border-blue-300'
                              }`}
                            >
                              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3 ${
                                formData.financingType === type.id ? 'bg-blue-600' : 'bg-slate-100'
                              }`}>
                                {IconComponent && (
                                  <IconComponent className={`w-5 h-5 sm:w-6 sm:h-6 ${
                                    formData.financingType === type.id ? 'text-white' : 'text-slate-600'
                                  }`} />
                                )}
                              </div>
                              <p className={`text-xs sm:text-sm font-medium leading-tight ${
                                formData.financingType === type.id ? 'text-blue-700' : 'text-slate-700'
                              }`}>
                                {type.title}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6">
                      <div>
                        <Label htmlFor="amount">Gewünschter Betrag *</Label>
                        <Input
                          id="amount"
                          type="number"
                          placeholder="z.B. 50000"
                          value={formData.amount}
                          onChange={(e) => updateFormData('amount', e.target.value)}
                          className="mt-2"
                          data-testid="inquiry-amount-input"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Financial Data */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-bold text-slate-900 mb-6">Finanzdaten</h2>

                    <div>
                      <Label htmlFor="employmentType">Beschäftigungsverhältnis *</Label>
                      <Select value={formData.employmentType} onValueChange={(value) => updateFormData('employmentType', value)}>
                        <SelectTrigger className="mt-2" data-testid="inquiry-employment-select">
                          <SelectValue placeholder="Bitte wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="angestellt">Angestellt</SelectItem>
                          <SelectItem value="beamter">Beamter</SelectItem>
                          <SelectItem value="selbststaendig">Selbstständig</SelectItem>
                          <SelectItem value="freiberufler">Freiberufler</SelectItem>
                          <SelectItem value="rentner">Rentner</SelectItem>
                          <SelectItem value="student">Student</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="netIncome">Monatliches Nettoeinkommen *</Label>
                        <Input
                          id="netIncome"
                          type="number"
                          placeholder="z.B. 3500"
                          value={formData.netIncome}
                          onChange={(e) => updateFormData('netIncome', e.target.value)}
                          className="mt-2"
                          data-testid="inquiry-net-income-input"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="eigenkapital">Eigenkapital (in €) *</Label>
                        <Input
                          id="eigenkapital"
                          type="number"
                          placeholder="z. B. 20.000"
                          value={formData.eigenkapital}
                          onChange={(e) => updateFormData('eigenkapital', e.target.value)}
                          className="mt-2"
                          data-testid="inquiry-eigenkapital-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="desiredMonthlyRate">Monatliche Wunschrate (in €) *</Label>
                        <Input
                          id="desiredMonthlyRate"
                          type="number"
                          placeholder="z. B. 900"
                          value={formData.desiredMonthlyRate}
                          onChange={(e) => updateFormData('desiredMonthlyRate', e.target.value)}
                          className="mt-2"
                          data-testid="inquiry-monthly-rate-input"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="permanentContract">Unbefristetes Arbeitsverhältnis? *</Label>
                        <Select value={formData.permanentContract} onValueChange={(value) => updateFormData('permanentContract', value)}>
                          <SelectTrigger className="mt-2" data-testid="inquiry-permanent-contract-select">
                            <SelectValue placeholder="Bitte wählen" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ja">Ja</SelectItem>
                            <SelectItem value="nein">Nein</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Personal Data */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-bold text-slate-900 mb-6">Persönliche Daten</h2>

                    <div>
                      <Label>Anrede *</Label>
                      <div className="flex gap-4 mt-2">
                        {['Herr', 'Frau', 'Divers'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => updateFormData('salutation', option)}
                            data-testid={`salutation-${option}`}
                            className={`px-6 py-2 rounded-lg border-2 transition-all ${
                              formData.salutation === option
                                ? 'border-blue-600 bg-blue-50 text-blue-700'
                                : 'border-slate-200 hover:border-blue-300'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="firstName">Vorname *</Label>
                        <Input
                          id="firstName"
                          value={formData.firstName}
                          onChange={(e) => updateFormData('firstName', e.target.value)}
                          className="mt-2"
                          data-testid="inquiry-first-name-input"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Nachname *</Label>
                        <Input
                          id="lastName"
                          value={formData.lastName}
                          onChange={(e) => updateFormData('lastName', e.target.value)}
                          className="mt-2"
                          data-testid="inquiry-last-name-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="birthDate">Geburtsdatum *</Label>
                        <Input
                          id="birthDate"
                          type="date"
                          value={formData.birthDate}
                          onChange={(e) => updateFormData('birthDate', e.target.value)}
                          className="mt-2"
                          data-testid="inquiry-birthdate-input"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="postalCode">PLZ *</Label>
                        <Input
                          id="postalCode"
                          value={formData.postalCode}
                          onChange={(e) => updateFormData('postalCode', e.target.value)}
                          className="mt-2"
                          data-testid="inquiry-postal-code-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email">E-Mail</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => updateFormData('email', e.target.value)}
                          className="mt-2"
                          data-testid="inquiry-email-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Telefon *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => updateFormData('phone', e.target.value)}
                          className="mt-2"
                          data-testid="inquiry-phone-input"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Contact */}
                {currentStep === 4 && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-bold text-slate-900 mb-6">Kontaktaufnahme</h2>

                    <div>
                      <Label>Bevorzugte Kontaktart *</Label>
                      <div className="flex flex-wrap gap-4 mt-2">
                        {['Telefon', 'E-Mail', 'Beides'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => updateFormData('preferredContact', option)}
                            data-testid={`preferred-contact-${option}`}
                            className={`px-6 py-2 rounded-lg border-2 transition-all ${
                              formData.preferredContact === option
                                ? 'border-blue-600 bg-blue-50 text-blue-700'
                                : 'border-slate-200 hover:border-blue-300'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>

                    {(formData.preferredContact === 'Telefon' || formData.preferredContact === 'Beides') && (
                      <div>
                        <Label htmlFor="callbackTime">Bevorzugte Rückrufzeit *</Label>
                        <Select value={formData.callbackTime} onValueChange={(value) => updateFormData('callbackTime', value)}>
                          <SelectTrigger className="mt-2" data-testid="inquiry-callback-time-select">
                            <SelectValue placeholder="Bitte wählen" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="morning">Vormittags (9-12 Uhr)</SelectItem>
                            <SelectItem value="afternoon">Nachmittags (12-15 Uhr)</SelectItem>
                            <SelectItem value="evening">Spätnachmittags (15-18 Uhr)</SelectItem>
                            <SelectItem value="anytime">Jederzeit</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div>
                      <Label htmlFor="comments">Anmerkungen oder Fragen</Label>
                      <textarea
                        id="comments"
                        rows={4}
                        value={formData.comments}
                        onChange={(e) => updateFormData('comments', e.target.value)}
                        className="mt-2 w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Teilen Sie uns zusätzliche Informationen mit..."
                        data-testid="inquiry-comments-input"
                      />
                    </div>

                    {/* Privacy & Consent */}
                    <div className="space-y-4 pt-4 border-t border-slate-200">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="privacy"
                          checked={formData.privacyAccepted}
                          onCheckedChange={(checked) => updateFormData('privacyAccepted', checked)}
                          data-testid="inquiry-privacy-checkbox"
                        />
                        <label htmlFor="privacy" className="text-sm text-slate-600 cursor-pointer">
                          Ich habe die <a href="/datenschutz" className="text-blue-600 hover:underline">Datenschutzerklärung</a> gelesen und akzeptiere diese. *
                        </label>
                      </div>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="newsletter"
                          checked={formData.newsletterAccepted}
                          onCheckedChange={(checked) => updateFormData('newsletterAccepted', checked)}
                          data-testid="inquiry-newsletter-checkbox"
                        />
                        <label htmlFor="newsletter" className="text-sm text-slate-600 cursor-pointer">
                          Ich möchte über aktuelle Angebote und Neuigkeiten informiert werden (optional).
                        </label>
                      </div>
                    </div>

                    {/* Security Note */}
                    <div className="bg-slate-50 rounded-xl p-4 flex items-start gap-3">
                      <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-900 text-sm">Ihre Daten sind sicher</p>
                        <p className="text-xs text-slate-600">
                          SSL-verschlüsselte Übertragung. Wir geben Ihre Daten nicht an unbefugte Dritte weiter.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-10 pt-6 border-t border-slate-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    disabled={currentStep === 1 || isSubmitting}
                    className="gap-2"
                    data-testid="inquiry-back-btn"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Zurück
                  </Button>

                  {currentStep < totalSteps ? (
                    <Button
                      type="button"
                      onClick={handleNext}
                      className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                      data-testid="inquiry-next-btn"
                    >
                      Weiter
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      className="bg-green-600 hover:bg-green-700 text-white gap-2"
                      disabled={!formData.privacyAccepted || isSubmitting}
                      data-testid="inquiry-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Wird gesendet...
                        </>
                      ) : (
                        <>
                          Anfrage absenden
                          <CheckCircle2 className="w-4 h-4" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </div>
            </>
          )}
        </div>
      </main>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default InquiryPage;
